/*
 * Show and hide the desktop filter rail on the collection and search pages.
 *
 * THIS FILE OWNS ALMOST NOTHING, AND THAT IS THE DESIGN. The rail's default state is decided in
 * snippets/givino-collection-filters-styles.liquid, keyed off the viewport and off the filters block's
 * "Filter panel on desktop" setting (data-default on this element): closed at every width from 750
 * by default, or open at >= 990 and closed at 750 to 989 when set to "open"; inert below 750 where
 * the native drawer takes over. All this module ever does is add or
 * remove two override classes on <results-list>, `gv-filters-open` and `gv-filters-closed`.
 *
 * The reason is first paint. The module is loaded with type="module" fetchpriority="low", so it
 * runs well after the page has painted. If the open state were a class this file added, then on a
 * rail that defaults to OPEN the grid would paint full width and then jump 324px sideways the
 * moment the class landed, on every collection and search page load. Because the server sends no
 * state class at all, the CSS default is already right when the page paints and there is nothing
 * to jump. It also means the rail still renders open at >= 990 with JavaScript switched off.
 *
 * THE MORPH IS THE THING THAT BREAKS NAIVE VERSIONS OF THIS COMPONENT. Applying a facet or a sort
 * re-renders the section through assets/facets.js, which hands off to sectionRenderer.renderSection
 * and morphs the result into the page. That morph resets <results-list>'s class attribute to the
 * server value, wiping whichever override class was on it, and it replaces the toggle button, with
 * the server's aria-expanded and label text. It does NOT re-run connectedCallback, so there is no
 * second chance to set anything up. Two consequences, and both are load-bearing:
 *
 *   1. The click listener is delegated to the host element, never attached to the button. A
 *      listener on the button dies with the first filter the shopper applies, and the toggle then
 *      does nothing for the rest of the visit with no error anywhere.
 *   2. A MutationObserver on <results-list>'s class attribute puts the shopper's choice back after
 *      each morph, and re-syncs the newly substituted button's aria-expanded and label. That
 *      callback writes the very attribute it is watching, so it has to be able to recognise its
 *      own work or it re-asserts forever; see handleClassChange.
 *
 * TWO MEDIA QUERIES, BECAUSE THEY ANSWER DIFFERENT QUESTIONS. 750 is "is this component active at
 * all, or is the native drawer in charge". 990 is "which way does the CSS default point". One
 * query cannot do both jobs: below 750 the override classes have to come off entirely so the
 * drawer is untouched, and at 750 to 989 the component is very much active but defaulting closed.
 *
 * NOTHING HERE THROWS OUT OF A SHOPPER INTERACTION. sessionStorage throws outright in some privacy
 * modes, so every access is wrapped and answers null; the rail then still toggles, the choice just
 * stops surviving a morph. Every element lookup is null checked, because the pane, the button and
 * the label are all re-rendered by a morph this file does not control.
 */

(function givinoCollectionFilters() {
  /* These three are also written in the parser-time restore in blocks/filters.liquid, which runs
     before this module loads; gate:grid-columns fails when the two copies disagree. */
  const STORAGE_KEY = 'givinoFiltersOpen';
  const OPEN_CLASS = 'gv-filters-open';
  const CLOSED_CLASS = 'gv-filters-closed';

  /* The mobile drawer carries the same facets-block-wrapper--vertical class and is told apart only
     by its id, so this exclusion belongs on every selector that reaches for the pane. */
  const PANE = '.facets-block-wrapper--vertical:not(#filters-drawer)';

  /* type="hidden" is excluded on purpose. Calling focus() on a hidden input succeeds silently and
     moves nothing, which would leave a keyboard shopper's focus behind on the toggle with no clue
     that the rail had opened.

     [tabindex] is included for the same reason, and it is not hypothetical: the applied-filter
     chips are <facet-remove-component tabindex="0" role="button">, so on any collection page with
     a filter applied every chip was skipped, and the first element that DID match was the rail's
     Clear all button, which is display:none until a filter is active. focus() on a display:none
     element is a silent no-op. Opening the rail therefore moved focus nowhere on exactly the pages
     where a keyboard shopper most needs it to move. Excluding hidden inputs was never the whole
     job; the check below is. */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function readPreference() {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  function writePreference(value) {
    try {
      sessionStorage.setItem(STORAGE_KEY, value);
    } catch (error) {
      /* Storage is blocked. The rail still opens and closes; the choice simply will not survive
         the next section morph, which is a smaller loss than a thrown error mid filter. */
    }
  }

  class GivinoCollectionFilters extends HTMLElement {
    constructor() {
      super();
      this.results = null;

      /* Bound once here rather than in connectedCallback, so that the references
         disconnectedCallback removes are the same objects connectedCallback added. Re-binding on
         every connect would leak a listener each time the element is moved in the DOM. */
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleActiveChange = this.handleActiveChange.bind(this);
      this.handleWideChange = this.handleWideChange.bind(this);
      this.handleClassChange = this.handleClassChange.bind(this);
      this.handleButtonChange = this.handleButtonChange.bind(this);
    }

    connectedCallback() {
      this.results = this.closest('results-list');
      if (!this.results) {
        console.warn(
          'givino-collection-filters: no <results-list> ancestor, so there is nothing to toggle. Left inert.'
        );
        return;
      }

      this.mqActive = window.matchMedia('(min-width: 750px)');
      this.mqWide = window.matchMedia('(min-width: 990px)');

      /* Both of these are the section Liquid's side of the contract, and both fail silently: the
         page renders perfectly and the toggle simply does nothing anyone can trace. Say so once,
         at connect, rather than on every click. */
      if (!this.results.classList.contains('gv-coll-toggle')) {
        console.warn(
          'givino-collection-filters: <results-list> is missing the gv-coll-toggle class, so no rule in snippets/givino-collection-filters-styles.liquid applies. The section renders that class from the server.'
        );
      }
      if (!this.pane) {
        console.warn(
          'givino-collection-filters: no vertical facets pane in this results list, so the button aria-controls target does not exist.'
        );
      }

      const stored = readPreference();
      this.preference = stored === '1' ? true : stored === '0' ? false : null;

      this.addEventListener('click', this.handleClick);
      document.addEventListener('keydown', this.handleKeydown);
      this.mqActive.addEventListener('change', this.handleActiveChange);
      this.mqWide.addEventListener('change', this.handleWideChange);

      this.classObserver = new MutationObserver(this.handleClassChange);
      this.classObserver.observe(this.results, { attributes: true, attributeFilter: ['class'] });

      /* A SECOND observer, on the button, because the first one cannot see this case.
         The morph resets the button to the server's markup, which hard-codes aria-expanded="true"
         (correct at >= 990, where most of this traffic is). When the shopper has NO stored
         preference, <results-list> carries no override class, so the morph does not change its
         class attribute at all and the class observer never fires. At 750 to 989, where the CSS
         default is closed, that leaves a visibly shut rail whose button says it is expanded, and
         nothing corrects it for the rest of the visit.
         syncButton() is a no-op when the value already matches, so this cannot feed itself. */
      this.buttonObserver = new MutationObserver(this.handleButtonChange);
      this.buttonObserver.observe(this, {
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-expanded'],
      });

      /* With no stored choice this writes no class at all and leaves the CSS default standing,
         which is the whole point. The button still has to be told what that default resolved to,
         because the server could not know the viewport when it rendered the label. */
      this.syncClasses();
      this.syncButton();
    }

    disconnectedCallback() {
      if (!this.results) return;
      document.removeEventListener('keydown', this.handleKeydown);
      this.mqActive.removeEventListener('change', this.handleActiveChange);
      this.mqWide.removeEventListener('change', this.handleWideChange);
      if (this.classObserver) this.classObserver.disconnect();
      if (this.buttonObserver) this.buttonObserver.disconnect();
    }

    get button() {
      return this.querySelector('.gv-filters__toggle');
    }


    get pane() {
      return this.results ? this.results.querySelector(PANE) : null;
    }

    /* The filters block's "Filter panel on desktop" setting, rendered by the server as
       data-default. "closed" (the default since 2026-10-01, Matt) means shut at every width from
       750; "open" means open from 990 and shut at 750 to 989. Read on each call rather than cached:
       a section morph rewrites this element's attributes to the server's. */
    get defaultsOpen() {
      return this.dataset.default === 'open';
    }

    /* The state the shopper is actually looking at: their stored choice if they made one, and
       otherwise whatever the stylesheet defaults to at this width. */
    get isOpen() {
      if (this.preference !== null) return this.preference;
      return this.defaultsOpen && this.mqWide.matches;
    }

    syncClasses() {
      const classes = this.results.classList;

      /* No stored choice means no override, so the CSS default stands. Below 750 the classes come
         off too, so the native drawer is untouched by anything this component decided. The stored
         preference is deliberately NOT cleared there: a shopper who closed the rail on a laptop,
         then rotated a tablet into portrait and back, should find it closed again. */
      if (!this.mqActive.matches || this.preference === null) {
        classes.remove(OPEN_CLASS, CLOSED_CLASS);
        return;
      }

      classes.toggle(OPEN_CLASS, this.preference);
      classes.toggle(CLOSED_CLASS, !this.preference);
    }

    /* The re-entrancy guard for handleClassChange, kept as the exact mirror of syncClasses so the
       two cannot disagree about what "already correct" means. */
    classesAreCurrent() {
      const classes = this.results.classList;
      const hasOpen = classes.contains(OPEN_CLASS);
      const hasClosed = classes.contains(CLOSED_CLASS);

      if (!this.mqActive.matches || this.preference === null) return !hasOpen && !hasClosed;
      return this.preference ? hasOpen && !hasClosed : hasClosed && !hasOpen;
    }

    /* aria-expanded is the ONLY thing this has left to do, and that is deliberate.
     *
     * The visible label is two spans swapped by the media queries in
     * snippets/givino-collection-filters-styles.liquid, not one span rewritten here. The rail's
     * default differs by viewport and the server cannot know the viewport, so a JS-written label
     * is briefly wrong at 750 to 989 on every load, and wrong forever if the module fails. CSS
     * already knows the width. No CSS can set an attribute, so aria-expanded stays here. */
    syncButton() {
      const button = this.button;
      if (!button) return;
      const wanted = this.isOpen ? 'true' : 'false';
      /* Only write when it differs. The observer below watches this very attribute, so an
         unconditional setAttribute would queue a callback for every sync and the two would drive
         each other. */
      if (button.getAttribute('aria-expanded') !== wanted) {
        button.setAttribute('aria-expanded', wanted);
      }
    }

    setOpen(open, moveFocus) {
      this.preference = open;
      writePreference(open ? '1' : '0');
      this.syncClasses();
      this.syncButton();
      if (open && moveFocus) this.focusPane();
    }

    focusPane() {
      const pane = this.pane;
      if (!pane) return;

      /* The class that makes the pane visible was set one statement ago. An element still carrying
         visibility:hidden cannot take focus, and nothing pins down whether the engine has
         recomputed style by the time focus() asks. Reading a layout property forces that flush, so
         the answer stops depending on the browser. */
      void pane.offsetWidth;

      /* Matching the selector is not enough: a candidate can be display:none, and focus() on one
         succeeds silently while moving nothing. getClientRects().length is the cheapest honest
         test for "this element has a box on the page". */
      const target = [...pane.querySelectorAll(FOCUSABLE)].find((el) => el.getClientRects().length);
      if (target) {
        target.focus();
        return;
      }

      /* Nothing focusable with a box, which means a rail of collapsed panels and nothing applied.
         The first facet heading is a <summary>, and landing there still tells a screen reader
         where it now is. */
      const fallback = pane.querySelector('summary');
      if (fallback) fallback.focus();
    }

    handleClick(event) {
      const trigger = event.target instanceof Element ? event.target.closest('.gv-filters__toggle') : null;
      if (!trigger) return;

      /* Focus moves into the pane on open. On close it is left where it is, which is on the toggle
         itself, because the toggle is what the shopper just clicked. */
      this.setOpen(!this.isOpen, true);
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      if (!this.mqActive.matches || !this.isOpen) return;

      /* Somebody else has already answered this Escape. Every other Escape handler in this theme
         checks the same thing, assets/theme-drawer.js most explicitly. Without it, closing the
         Sort popover, the mega menu, the search modal or the cart drawer also collapsed the rail
         and reflowed the grid behind whatever the shopper was actually dismissing. */
      if (event.defaultPrevented) return;

      /* And Escape only belongs to the rail when the rail is where the shopper is. The listener
         has to be on the document, because focus legitimately sits deep inside the pane, but that
         is not a licence to answer every Escape on the page. */
      const active = document.activeElement;
      const inside = active && (this.contains(active) || (this.pane && this.pane.contains(active)));
      if (!inside) return;

      /* The mobile drawer is a <theme-drawer> wrapping a <dialog>, and the custom element has no
         `open` property of its own: asking it returns undefined, so a guard written against the
         element always passes and one Escape closes the drawer and the rail together. The inner
         dialog is the only thing that knows. Getting this backwards the other way, by guarding on
         something always truthy, means the rail stops answering Escape at all. */
      const drawerDialog = document.querySelector('#filters-drawer dialog');
      if (drawerDialog && drawerDialog.open) return;

      const wasInPane = this.pane && this.pane.contains(active);
      this.setOpen(false, false);

      /* The pane is about to become non-interactive, so focus that was inside it has to be put
         somewhere deliberate. Focus that was already on the toggle stays there: moving it to the
         element it is already on is a no-op that reads as a jump in a screen reader. */
      if (wasInPane) {
        const button = this.button;
        if (button) button.focus();
      }
    }

    handleActiveChange() {
      /* Crossing 750 in either direction. Below it the override classes come off so the drawer is
         unaffected; above it the remembered choice goes back on. syncClasses already encodes both,
         so this is one call rather than a branch that could drift from it. */
      this.syncClasses();
      this.syncButton();
    }

    handleWideChange() {
      /* With no stored choice the button is describing the CSS default, and that default flips at
         990, so the label and aria-expanded have to move with it or the button starts lying. */
      this.syncButton();
    }

    handleButtonChange() {
      this.syncButton();
    }

    handleClassChange() {
      /* Runs after every section morph, because the morph rewrites the class attribute this is
         watching. Re-assert the shopper's choice, then re-sync the button, which the morph has
         just replaced with the server's aria-expanded and label.
         The syncClasses call below writes the attribute that triggered this callback, so without
         the guard the first re-assertion queues a second callback, which re-asserts again, and the
         observer never settles. syncButton touches no attribute on <results-list>, so it is safe
         to run unconditionally, and it has to: after a morph the classes are often already right
         while the button is not. */
      if (!this.classesAreCurrent()) this.syncClasses();
      this.syncButton();
    }
  }

  if (!customElements.get('givino-collection-filters')) {
    customElements.define('givino-collection-filters', GivinoCollectionFilters);
  }
})();
